// @flow
import React from 'react'
import classNames from 'classnames'
import AbstractFormField from './AbstractFormField'
import type { ValidationErrorType } from '../../Types/ValidationError'
import type { HTMLInputType, InputType } from '../../Types/InputTypes'
import { HTMLInputTypes } from '../../Enums'
import { formatInputValue } from '../../InputHelpFunctions'
import FieldError from '../form/Error'
import { isEmpty } from 'ramda'
import { resourcesRequestSuccessR } from '../../Redux/ProductRedux'

type StringInputProps = {
  autoFocus?: boolean,
  className: string,
  enabled?: boolean,
  id: string,
  inputClassName?: string,
  inputType?: InputType,
  isPristine?: boolean,
  maxLength?: number,
  name: string,
  onChange: (value: string) => void,
  prefix?: string,
  textMode?: boolean,
  validationErrors: ValidationErrorType,
  value: string,
}

export type StringInputState = {
  pristine: boolean,
  prevPropsValue: string,
  prevIsPristine: boolean,
  value: string,
}

/**
 * Component for rendering text input
 * @example ./docs/String.md
 */
export default class StringInput extends AbstractFormField<StringInputProps, StringInputState> {
  static defaultProps = {
    autoFocus: false,
    enabled: true,
    inputType: HTMLInputTypes.TEXT,
    isPristine: false,
    prefix: '',
    textMode: false,
    wrapperClassName: 'd-inline-block',
  }

  constructor(props: StringProps) {
    super(props)
    this.state = {
      prevPropsValue: '',
      pristine: true,
      prevIsPristine: true,
      value: props.value || '',
    }
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      setTimeout(() => {
        this.input.focus()
      }, 10)
    }
  }

  componentDidUpdate(prevProps: StringProps) {
    if (this.props.value !== prevProps.value && this.props.value !== this.state.value) {
      this.setState({ value: this.props.value })
    }
  }

  handleOnBlur = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const { inputType, onChange } = this.props
    const formattedValue = formatInputValue(inputType, event.target.value)
    this.setState({ value: formattedValue })
    onChange(formattedValue)
    this.onBlur(event)
  }

  handleOnChange = (event: SyntheticInputEvent<HTMLInputElement>) => this.setState({ value: event.target.value })

  render() {
    const {
      enabled,
      id,
      inputClassName,
      inputType,
      maxLength,
      name,
      prefix,
      textMode,
      templateVariables,
      validationErrors,
      value,
    } = this.props

    if (textMode) {
      return <span>{value}</span>
    }

    const error = !this.isPristine() && validationErrors && !isEmpty(validationErrors[name]) && validationErrors[name]
    const classes = ['form-control', inputClassName || '', error && 'is-invalid']

    const fieldInputType: HTMLInputType = Object.values(HTMLInputTypes).includes(inputType)
      ? inputType
      : HTMLInputTypes.TEXT

    return (
      <div className={`${this.props.wrapperClassName} w-100`}>
        <div className="form-group mb-0 d-flex flex-wrap align-items-center">
          <input
            className={classNames(classes)}
            data-hj-whitelist
            id={id}
            maxLength={maxLength}
            name={name}
            onBlur={this.handleOnBlur}
            onChange={this.handleOnChange}
            readOnly={!enabled}
            ref={(input) => {
              this.input = input
            }}
            type={fieldInputType}
            value={this.state.value}
          />
          <FieldError
            error={error}
            name={name}
            onHideError={this.hideError}
            prefix={prefix}
            templateVariables={templateVariables}
          />
        </div>
      </div>
    )
  }
}
